import { useState, useCallback, useEffect, useMemo } from 'react';

const SETTINGS_STORAGE_KEY = 'cesiumViewerSettings';

/**
 * Default settings values.
 */
const DEFAULT_SETTINGS = {
  // Point cloud visualization settings
  pointSize: 3,
  useBrowserRecommendationResolution: false,
  eyeDomeLighting: false,
  eyeDomeLightingStrength: 0.3,
  eyeDomeLightingRadius: 0.2,

  // World settings
  globeLighting: false,
  atmosphereRendering: true,

  // Terrain settings
  terrainRendering: true,
  contourLinesEnabled: false,
  contourSpacing: 1,
  thickLineInterval: 5,
  lineThickness: 0.05,

  // Camera settings
  fieldOfView: 60,
  zoomFactor: 5.0,

  // Rendering settings
  visualFidelity: 1, // 0 = quality, 1 = balanced, 2 = performance
  ssaoEnabled: false,
  ssaoIntensity: 3.0,
  fxaaEnabled: false,
};

// Setter names that don't follow simple camelCase→PascalCase (e.g. acronyms)
const SETTER_NAME_OVERRIDES = {
  ssaoEnabled: 'setSSAOEnabled',
  ssaoIntensity: 'setSSAOIntensity',
  fxaaEnabled: 'setFXAAEnabled',
};

function toSetterName(key) {
  return SETTER_NAME_OVERRIDES[key] || ('set' + key.charAt(0).toUpperCase() + key.slice(1));
}

/**
 * Hook to group viewer settings states with localStorage persistence.
 * Uses a single state object instead of individual useState calls.
 *
 * @returns {Object} Settings state and management methods
 */
export function useViewerSettings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  /**
   * Update one or more settings by key.
   */
  const updateSettings = useCallback((updates) => {
    setSettings(prev => ({ ...prev, ...updates }));
  }, []);

  // Memoize individual setters so they have stable references
  const setters = useMemo(() => {
    const result = {};
    for (const key of Object.keys(DEFAULT_SETTINGS)) {
      result[toSetterName(key)] = (value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
      };
    }
    return result;
  }, []);

  /**
   * Get all current settings as an object.
   */
  const getAllSettings = useCallback(() => ({ ...settings }), [settings]);

  /**
   * Save current settings to localStorage.
   */
  const saveSettings = useCallback(() => {
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
      console.log('Settings saved successfully');
    } catch (error) {
      console.warn('Failed to save settings:', error);
    }
  }, [settings]);

  /**
   * Load settings from localStorage.
   * @returns {boolean} True if settings were loaded successfully
   */
  const loadSettings = useCallback(() => {
    try {
      const savedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        console.log('Loading saved settings:', parsed);
        // Only apply keys that exist in defaults (ignore stale keys)
        const filtered = {};
        for (const key of Object.keys(DEFAULT_SETTINGS)) {
          if (parsed[key] !== undefined) {
            filtered[key] = parsed[key];
          }
        }
        setSettings(prev => ({ ...prev, ...filtered }));
        console.log('Settings loaded successfully');
        return true;
      }
    } catch (error) {
      console.warn('Failed to load settings:', error);
    }
    return false;
  }, []);

  /**
   * Reset all settings to defaults.
   */
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  // Load settings on component mount (defer to next tick to avoid overwriting defaults)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadSettings();
    }, 0);
    return () => clearTimeout(timer);
  }, [loadSettings]);

  return {
    // Individual state values
    ...settings,

    // Individual setters (stable references via useMemo)
    ...setters,

    // Generic update method
    updateSettings,

    // Persistence methods
    saveSettings,
    loadSettings,
    resetSettings,
    getAllSettings,
  };
}

export default useViewerSettings;
